import React, { useState } from 'react';
import { Modal, Input, Flex, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useBulkDelayRtoOrders } from '@/hooksApi/useInventory';
import { InventoryOrder } from '@/types/inventory';

const { TextArea } = Input;

interface DelayRtoConfirmModalProps {
  open: boolean;
  orders: InventoryOrder[];
  onClose: () => void;
  onSuccess?: () => void;
}

const DelayRtoConfirmModal: React.FC<DelayRtoConfirmModalProps> = ({
  open,
  orders,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  const { mutate: bulkDelayRto, isLoading } = useBulkDelayRtoOrders();

  const handleConfirm = () => {
    if (orders.length === 0) return;

    bulkDelayRto(
      { orders_uid: orders.map((o) => o.uid), reason: reason.trim() || undefined },
      {
        onSuccess: () => {
          message.success(String(t('return_success')));
          queryClient.invalidateQueries(['inventory']);
          setReason('');
          onClose();
          onSuccess?.();
        },
        onError: () => {
          message.error(String(t('error_title')));
        },
      },
    );
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  return (
    <Modal
      className='sd-font'
      title={
        <div className='text-base font-semibold text-sd-foreground'>
          {String(t('confirm_return'))}
        </div>
      }
      open={open}
      onOk={handleConfirm}
      onCancel={handleClose}
      okText={String(t('confirm'))}
      cancelText={String(t('cancel'))}
      okButtonProps={{ loading: isLoading }}
      centered
      destroyOnClose
      styles={{
        content: { borderRadius: '12px' },
        mask: { backgroundColor: 'rgba(0,0,0,0.6)' },
      }}
    >
      <Flex vertical gap={16} className='py-2'>
        <p className='text-sm text-gray-600 m-0'>
          {orders.length === 1
            ? String(t('confirm_return_message_single'))
            : String(t('confirm_return_message'))}
        </p>

        {orders.length > 0 && (
          <div className='rounded-lg border border-solid border-[#e8e6e3] bg-[#f9f8f6] px-4 py-3'>
            <Flex vertical gap={6}>
              {orders.map((order) => (
                <div key={order.uid} className='flex items-center gap-2'>
                  <span className='inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400' />
                  <span className='text-sm font-medium text-sd-foreground'>
                    {order.platform_code || order.uid}
                  </span>
                </div>
              ))}
            </Flex>
          </div>
        )}

        <Flex vertical gap={6}>
          <label className='text-sm font-medium text-sd-foreground'>
            {String(t('return_reason'))}
          </label>
          <TextArea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={String(t('return_reason'))}
            className='rounded-lg'
          />
        </Flex>
      </Flex>
    </Modal>
  );
};

export default DelayRtoConfirmModal;
